import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
} from '@mui/material';
import {
  ColorLens as ColorIcon,
} from '@mui/icons-material';

const ColorEmojiPicker = ({
  color,
  emoji,
  onColorChange,
  onEmojiChange,
  emojiCategories,
  previewLabel,
  showPreview = true,
  colorLabel = "Color",
  emojiLabel = "Select Emoji"
}) => {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [customEmojiMode, setCustomEmojiMode] = useState(false);
  const [customEmojiInput, setCustomEmojiInput] = useState('');

  // Predefined color options
  const colorOptions = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
    '#14B8A6', '#F43F5E', '#8E8E93', '#FF9500', '#30D158',
    '#BF5AF2', '#FF2D92', '#5AC8FA', '#FFCC00', '#FF3B30'
  ];

  // Helper function to get contrast color
  const getContrastColor = (hex) => {
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Color picker component
  const ColorPicker = ({ color, onChange }) => {
    const [localColor, setLocalColor] = useState(color);
    
    // Update local color when prop changes
    useEffect(() => {
      setLocalColor(color);
    }, [color]);

    const handleColorChange = (newColor) => {
      setLocalColor(newColor);
      onChange(newColor);
    };

    const handleInputChange = (e) => {
      const value = e.target.value;
      // Allow typing # and up to 6 hex characters
      if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
        setLocalColor(value);
        // Only call onChange if it's a valid hex color
        if (value.length === 7 || value.length === 4) {
          onChange(value);
        }
      }
    };

    const handleInputBlur = () => {
      // Ensure we have the final color on blur
      if (localColor !== color) {
        onChange(localColor);
      }
    };

    return (
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ColorIcon />}
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
            sx={{ 
              backgroundColor: color,
              color: getContrastColor(color),
              minWidth: 120,
              '&:hover': {
                backgroundColor: color,
                opacity: 0.8
              }
            }}
          >
            {color}
          </Button>
          <TextField
            size="small"
            label="Hex Color"
            value={localColor}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            sx={{ 
              minWidth: 100,
              '& .MuiInputBase-root': {
                height: 40
              }
            }}
            placeholder="#FFFFFF"
          />
        </Box>
        {colorPickerOpen && (
          <Box sx={{ 
            position: 'absolute', 
            top: '100%', 
            left: 0, 
            zIndex: 1000,
            mt: 1,
            p: 2,
            bgcolor: 'white',
            boxShadow: 3,
            borderRadius: 1,
            minWidth: 280
          }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Select a color:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {colorOptions.map((colorOption) => (
                <Box
                  key={colorOption}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: colorOption,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: color === colorOption ? '3px solid #000' : '1px solid #ccc',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.1s'
                    }
                  }}
                  onClick={() => {
                    handleColorChange(colorOption);
                    setColorPickerOpen(false);
                  }}
                />
              ))}
            </Box>
            <Button 
              onClick={() => setColorPickerOpen(false)}
              size="small"
              sx={{ mt: 1, width: '100%' }}
            >
              Done
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="body2" gutterBottom>
          {emojiLabel}
        </Typography>
        <Box sx={{ 
          maxHeight: 200, 
          overflowY: 'auto',
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 2
        }}>
          {Object.entries(emojiCategories).map(([category, emojis]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {emojis.map(emojiOption => (
                  <Box
                    key={emojiOption}
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: emoji === emojiOption ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: emoji === emojiOption ? '#e3f2fd' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s',
                      fontSize: '16px'
                    }}
                    onClick={() => onEmojiChange(emojiOption)}
                  >
                    {emojiOption}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Custom Emoji Option */}
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          {!customEmojiMode ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCustomEmojiMode(true)}
              sx={{ textTransform: 'none' }}
            >
              Other Emoji
            </Button>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                label="Paste or type emoji"
                value={customEmojiInput}
                onChange={(e) => setCustomEmojiInput(e.target.value)}
                sx={{ width: 180 }}
                placeholder="e.g. 🧬"
                inputProps={{ maxLength: 4 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!customEmojiInput.trim()}
                onClick={() => {
                  onEmojiChange(customEmojiInput.trim());
                  setCustomEmojiMode(false);
                  setCustomEmojiInput('');
                }}
              >
                Use
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setCustomEmojiMode(false);
                  setCustomEmojiInput('');
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          Selected: {emoji}
        </Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" gutterBottom>
          {colorLabel}
        </Typography>
        <ColorPicker
          color={color}
          onChange={onColorChange}
        />
      </Grid>
      
      {showPreview && (
        <Grid item xs={12}>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              Preview:
            </Typography>
            <Chip
              label={`${emoji} ${previewLabel}`}
              sx={{
                backgroundColor: color,
                color: getContrastColor(color),
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ColorEmojiPicker;